/**
 * Tabs creates view area that has tabs made out of its children.
 * It only expects all its children to have 'tabLabel', 'tabLinkProps' props.
 * In addition, 'selected' prop specifies which tab is open and 'disabled' renders tab inaccessible.
 *
 * e.g.
 *  <EditLiveRoomWizardTabs>
 *    <Child tabLabel="Tab1" tabLinkProps={{ name: 'SomeTab1Page' }}>
 *      Tab1 stuff
 *    </Child>
 *    <Child tabLabel="Tab2" tabLinkProps={{ name: 'SomeTab2Page' }} selected >
 *      Tab2 stuff
 *    </Child>
 *  </EditLiveRoomWizardTabs>
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import TabNav from '../EditLiveRoomWizardTabNav/EditLiveRoomWizardTabNav';
import css from './EditLiveRoomWizardTabs.module.css';

const EditLiveRoomWizardTabs = props => {
  const {
    children,
    className,
    rootClassName,
    navRootClassName,
    navWrapperRootClassName,
    tabRootClassName,
    linkClassName,
    selectedLinkClassName,
    titleClassName,
    title,
    isNewListingFlow,
    tabGroups,
    showLogo,
  } = props;
  const rootClasses = rootClassName || css.root;
  const classes = classNames(rootClasses, className);

  const tabNavTabs = React.Children.map(children, child => {
    const { tabId, tabLabel, tabLinkProps } = child.props;

    // Child components need to have TabNav props included
    if (!tabId || !tabLabel || !tabLinkProps) {
      throw new Error(
        `Tabs component: a child component is missing required props.
        tabId: (${tabId})
        tabLabel: (${tabLabel})
        tabLinkProps: (${tabLinkProps})`
      );
    }

    return {
      id: tabId,
      text: child.props.tabLabel,
      linkProps: child.props.tabLinkProps,
      disabled: child.props.disabled,
      selected: child.props.selected,
    };
  });

  // Build grouped tabs by matching tab IDs from tabGroups config
  const tabNavGroups = tabGroups
    ? tabGroups.map(group => ({
        label: group.label,
        tabs: group.tabs
          .map(tabKey => tabNavTabs.find(t => t.id.endsWith(`_${tabKey}`)))
          .filter(Boolean),
      }))
    : null;

  const childArray = React.Children.toArray(children);
  const selectedTabPanel = childArray.find(c => c.props.selected);

  // One of the children needs to be selected
  if (!selectedTabPanel) {
    throw new Error(`Tabs component: one Child should have 'selected' prop.`);
  }

  return (
    <div className={classes}>
      <TabNav
        rootClassName={navRootClassName}
        navWrapperRootClassName={navWrapperRootClassName}
        tabs={tabNavTabs}
        tabRootClassName={tabRootClassName}
        linkClassName={linkClassName}
        selectedLinkClassName={selectedLinkClassName}
        titleClassName={titleClassName}
        title={title}
        isNewListingFlow={isNewListingFlow}
        tabGroups={tabNavGroups}
        showLogo={showLogo}
      />
      {selectedTabPanel}
    </div>
  );
};

const { node, string, bool } = PropTypes;

EditLiveRoomWizardTabs.defaultProps = {
  className: null,
  rootClassName: null,
  navRootClassName: null,
  navWrapperRootClassName: null,
  tabRootClassName: null,
  linkClassName: null,
  selectedLinkClassName: null,
  titleClassName: null,
  title: null,
  isNewListingFlow: false,
  showLogo: false,
};

EditLiveRoomWizardTabs.propTypes = {
  children: node.isRequired,
  className: string,
  rootClassName: string,
  navRootClassName: string,
  navWrapperRootClassName: string,
  tabRootClassName: string,
  linkClassName: string,
  selectedLinkClassName: string,
  titleClassName: string,
  title: string,
  isNewListingFlow: bool.isRequired,
  showLogo: bool,
};

export default EditLiveRoomWizardTabs;
